import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { API, logErr, inr, fmtErr } from "../../lib/api";

const ALL_SIZES = ["S", "M", "L", "XL", "XXL"];
const CATS = ["streetwear", "casual", "formals"];

const EMPTY = { name: "", category: "streetwear", subcategory: "", description: "", price: "",
  sale_price: "", images: "", colors: "", tags: "", featured: false, new_arrival: false, active: true };

export default function AdminProducts() {
  const [products, setProducts] = useState(null);
  const [editing, setEditing] = useState(null); // null=closed, {}=new, product=edit
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const imageList = useMemo(
    () => form.images.split("\n").map((s) => s.trim()).filter(Boolean),
    [form.images]
  );

  const load = useCallback(() => API.get("/admin/products").then((r) => setProducts(r.data)).catch((err) => logErr("Failed to load products:", err)), [API, logErr]);
  useEffect(() => { load(); document.title = "Products | Admin"; }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (p) => {
    setForm({ ...p, price: String(p.price), sale_price: p.sale_price ? String(p.sale_price) : "",
      images: p.images.join("\n"), colors: p.colors.join(", "), tags: p.tags.join(", ") });
    setEditing(p);
  };

  const buildVariants = (f, existing = []) => {
    const sizes = ALL_SIZES;
    const colors = f.colors.split(",").map((x) => x.trim()).filter(Boolean);
    const variants = [];
    for (const size of sizes) for (const color of colors) {
      const ex = existing.find((v) => v.size === size && v.color === color);
      variants.push(ex || { size, color, stock: 10, sku: "" });
    }
    return variants;
  };

  const uploadImages = async (e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await API.post("/admin/upload", fd);
        setForm((prev) => ({ ...prev, images: (prev.images ? prev.images + "\n" : "") + r.data.url }));
      }
      toast.success(`${files.length} image(s) uploaded`);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name, category: form.category, subcategory: form.subcategory,
        description: form.description, price: parseFloat(form.price),
        sale_price: form.sale_price ? parseFloat(form.sale_price) : null,
        images: form.images.split("\n").map((x) => x.trim()).filter(Boolean),
        colors: form.colors.split(",").map((x) => x.trim()).filter(Boolean),
        sizes: ALL_SIZES,
        tags: form.tags.split(",").map((x) => x.trim()).filter(Boolean),
        featured: !!form.featured, new_arrival: !!form.new_arrival, active: !!form.active,
        variants: buildVariants(form, editing?.variants || []),
      };
      if (editing?.id) await API.put(`/admin/products/${editing.id}`, payload);
      else await API.post("/admin/products", payload);
      toast.success(editing?.id ? "Product updated" : "Product created");
      setEditing(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await API.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    load();
  };

  const inputCls = "w-full bg-ink border border-[#262626] px-3 py-2.5 text-sm outline-none focus:border-gold transition-colors";

  if (!products) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display font-black text-3xl tracking-tighter">Products <span className="text-muted-foreground text-lg font-normal">({products.length})</span></h1>
        <button onClick={openNew} data-testid="new-product-btn" className="btn-gold px-5 py-3 text-xs font-bold uppercase tracking-widest flex items-center gap-2">
          <Plus size={14} /> New product
        </button>
      </div>

      <div className="border border-[#262626] bg-ink-surface overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-[#262626]">
              <th className="p-4">Product</th><th className="p-4">Category</th><th className="p-4">Price</th>
              <th className="p-4">Stock</th><th className="p-4">Sold</th><th className="p-4">Flags</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-[#262626] hover:bg-ink-elevated/50" data-testid={`product-row-${p.slug}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={p.images[0]} alt="" className="h-10 w-8 object-cover" loading="lazy" />
                    <span className="font-bold text-xs">{p.name}</span>
                  </div>
                </td>
                <td className="p-4 text-xs text-muted-foreground capitalize">{p.category}</td>
                <td className="p-4 text-xs">
                  <span className="text-gold font-bold">{inr(p.sale_price || p.price)}</span>
                  {p.sale_price && <span className="text-muted-foreground line-through ml-2">{inr(p.price)}</span>}
                </td>
                <td className="p-4 text-xs">
                  <span className={p.total_stock === 0 ? "text-red-400 font-bold" : p.total_stock <= 15 ? "text-gold font-bold" : ""}>{p.total_stock}</span>
                </td>
                <td className="p-4 text-xs text-muted-foreground">{p.sold}</td>
                <td className="p-4">
                  <div className="flex gap-1">
                    {p.featured && <span className="text-[9px] bg-gold/15 text-gold px-1.5 py-0.5 uppercase">Feat</span>}
                    {p.new_arrival && <span className="text-[9px] bg-gold/15 text-gold px-1.5 py-0.5 uppercase">New</span>}
                    {!p.active && <span className="text-[9px] bg-red-400/15 text-red-400 px-1.5 py-0.5 uppercase">Off</span>}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(p)} data-testid={`edit-${p.slug}`} className="text-muted-foreground hover:text-gold"><Pencil size={14} /></button>
                    <button onClick={() => del(p.id)} data-testid={`delete-${p.slug}`} className="text-muted-foreground hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4" data-testid="product-form-modal">
          <form onSubmit={save} className="bg-ink-surface border border-[#262626] w-full max-w-2xl p-6 my-8 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-xl">{editing?.id ? "Edit product" : "New product"}</h2>
              <button type="button" onClick={() => setEditing(null)} data-testid="close-product-form"><X size={18} /></button>
            </div>
            <input data-testid="pf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Product name" className={inputCls} />
            <div className="grid grid-cols-2 gap-4">
              <select data-testid="pf-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid="pf-subcategory" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} placeholder="Subcategory (e.g. Hoodies)" className={inputCls} />
            </div>
            <textarea data-testid="pf-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Description" className={inputCls} />
            <div className="grid grid-cols-2 gap-4">
              <input data-testid="pf-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required placeholder="Price (₹)" className={inputCls} />
              <input data-testid="pf-sale-price" type="number" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} placeholder="Sale price (optional)" className={inputCls} />
            </div>
            <div className="border border-dashed border-gold/40 p-4 text-center hover:border-gold transition-colors" data-testid="image-upload-zone">
              <label className="cursor-pointer text-xs text-muted-foreground hover:text-gold transition-colors block">
                <input type="file" accept="image/*" multiple onChange={uploadImages} className="hidden" data-testid="pf-image-upload" />
                {uploading ? "Uploading images..." : "Upload product images (JPG/PNG, multiple allowed) — or paste URLs below"}
              </label>
            </div>
            {imageList.length > 0 && (
              <div className="flex gap-2 flex-wrap" data-testid="image-previews">
                {imageList.map((u) => (
                  <img key={u} src={u} alt="Product" className="h-14 w-14 object-cover border border-[#262626]" />
                ))}
              </div>
            )}
            <textarea data-testid="pf-images" value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} rows={2} placeholder="Image URLs (one per line)" className={inputCls} />
            <input data-testid="pf-colors" value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} placeholder="Colors (comma separated, e.g. Black, White)" className={inputCls} />
            <input data-testid="pf-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Tags (comma separated)" className={inputCls} />
            <div className="flex gap-6 text-xs">
              {[["featured", "Featured"], ["new_arrival", "New arrival"], ["active", "Active (visible)"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} className="accent-[#D4AF37]" data-testid={`pf-${k}`} /> {l}
                </label>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground">Variants (S–XXL × colors) are auto-generated; new variants start with 10 stock. Adjust stock in Inventory.</p>
            <button data-testid="pf-save-btn" className="btn-gold w-full py-3.5 text-xs font-bold uppercase tracking-[0.2em]">
              {editing?.id ? "Save changes" : "Create product"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
